import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  input,
  viewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  ButtonComponent,
  IconComponent,
  SoftSlotDirective,
  SlotPosition,
  TranslatePipe
} from '@softline/ui-core';
import { PageFilterRef } from '../../types/page-filter.ref';
import { PageFilterTemplatesDropdownComponent } from '../page-filter-templates-dropdown/page-filter-templates-dropdown.component';
import { PageFilterMenuBarComponent } from '../page-filter-menu-bar/page-filter-menu-bar.component';
import { PageFilterChipsComponent } from '../page-filter-chips/page-filter-chips.component';

/**
 * The page's filter toolbar, one card: Vorlagen → `softSlot="leading"` (page controls such as a
 * date range) → pinned filter dropdowns → `softSlot="trailing"` | Zurücksetzen · Filter, and the
 * chip row of the unpinned values below. Place it in `soft-page-header containerStyle="plain"`.
 */
@Component({
  selector: 'soft-page-filter-bar',
  imports: [
    NgTemplateOutlet,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    TranslatePipe,
    PageFilterTemplatesDropdownComponent,
    PageFilterMenuBarComponent,
    PageFilterChipsComponent
  ],
  templateUrl: './page-filter-bar.component.html',
  styleUrl: './page-filter-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterBarComponent {
  readonly config = input.required<PageFilterRef>();

  // shallow on purpose: a soft-ds-button inside a slot template carries its own softSlot="leading"
  // for its icon, which a descendants query would mistake for the bar's leading slot
  private readonly slots = contentChildren(SoftSlotDirective);
  protected readonly leading = this.slotAt('leading');
  protected readonly trailing = this.slotAt('trailing');

  private readonly templates = viewChild(PageFilterTemplatesDropdownComponent);

  constructor() {
    // the sidebar can be opened from anywhere - an open Vorlagen panel would float over it
    effect(() => {
      if (this.config().service.opened()) this.templates()?.close();
    });
  }

  reset(): void {
    // filter states only - the page's own controls in the slots are not ours to reset
    this.config().service.resetFilter();
  }

  open(): void {
    this.config().open();
  }

  private slotAt(position: SlotPosition) {
    return computed(
      () => this.slots().find((slot) => slot.position() === position)?.template ?? null
    );
  }
}
